import { captureConsoleIntegration, captureMessage, init } from '@sentry/browser';
import type { ErrorEvent } from '@sentry/browser';
import type { App, ComponentPublicInstance } from 'vue';

export interface AppStudioSentryOptions {
    dsn: string;
    enabled: boolean;
    environment: string;
    release: string;
}

type AppStudioSentrySdk = {
    captureConsoleIntegration: typeof captureConsoleIntegration;
    captureMessage: typeof captureMessage;
    init: typeof init;
};

const defaultSdk: AppStudioSentrySdk = {
    captureConsoleIntegration,
    captureMessage,
    init
};

const filteredValue = '[Filtered]';

function shouldFilterProperty(propertyName: string, parentPropertyName: string): boolean {
    const normalizedName = propertyName.replace(/[-_]/g, '').toLowerCase();
    if (
        normalizedName === 'authorization'
        || normalizedName === 'cookie'
        || normalizedName === 'setcookie'
        || normalizedName === 'requestverificationtoken'
        || normalizedName === 'xcsrftoken'
        || normalizedName === 'xxsrftoken'
        || normalizedName === 'csrftoken'
        || normalizedName === 'xsrftoken'
        || normalizedName === 'clientsecret'
        || normalizedName === 'auth0clientsecret'
    ) {
        return true;
    }

    const normalizedParentName = parentPropertyName.toLowerCase();
    return normalizedName === 'data' && (normalizedParentName === 'config' || normalizedParentName === 'request');
}

function filterSensitiveValues(value: unknown, parentPropertyName = '', visited = new WeakSet<object>()): void {
    if (value === null || typeof value !== 'object' || visited.has(value)) {
        return;
    }

    visited.add(value);
    if (Array.isArray(value)) {
        for (const item of value) {
            filterSensitiveValues(item, parentPropertyName, visited);
        }
        return;
    }

    const record = value as Record<string, unknown>;
    for (const [propertyName, propertyValue] of Object.entries(record)) {
        if (shouldFilterProperty(propertyName, parentPropertyName)) {
            record[propertyName] = filteredValue;
        } else {
            filterSensitiveValues(propertyValue, propertyName, visited);
        }
    }
}

function filterSentryEvent(event: ErrorEvent): ErrorEvent {
    filterSensitiveValues(event);
    return event;
}

export class AppStudioSentry {
    private initialized = false;

    public constructor(private readonly sdk: AppStudioSentrySdk = defaultSdk) {}

    public initialize(options: AppStudioSentryOptions | undefined): void {
        if (this.initialized || !options?.enabled || !options.dsn) {
            return;
        }

        this.sdk.init({
            beforeSend: filterSentryEvent,
            dsn: options.dsn,
            enabled: true,
            environment: options.environment,
            integrations: [this.sdk.captureConsoleIntegration({ levels: ['error'] })],
            release: options.release,
            initialScope: {
                tags: {
                    application: 'app-studio'
                }
            }
        });
        this.initialized = true;
    }

    public configureVueApp(app: App): void {
        if (!this.initialized) {
            return;
        }

        const originalWarnHandler = app.config.warnHandler;
        app.config.warnHandler = (message, instance, trace): void => {
            this.captureVueWarning(message, instance, trace);

            if (originalWarnHandler) {
                originalWarnHandler(message, instance, trace);
                return;
            }

            console.warn(`[Vue warn]: ${message}${trace}`);
        };
    }

    private captureVueWarning(
        message: string,
        instance: ComponentPublicInstance | null,
        trace: string
    ): void {
        const componentOptions = instance?.$options as { __name?: string; name?: string } | undefined;
        const componentName = componentOptions?.name ?? componentOptions?.__name ?? 'Anonymous';

        this.sdk.captureMessage(message, {
            level: 'warning',
            contexts: {
                vue: {
                    componentName,
                    trace
                }
            }
        });
    }
}
